import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { projectAPI, productAPI, applicationAPI } from '../../api/services';
import { Project, User } from '../../types';
import { useAppSelector } from '../../store/hooks';

const Req = () => <span className="text-red-500 ml-0.5">*</span>;

const FieldError: React.FC<{ msg?: string }> = ({ msg }) =>
  msg ? <p className="mt-1 text-xs text-red-600 flex items-center gap-1"><span>⚠</span>{msg}</p> : null;

const ProjectForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAppSelector((state) => state.auth);
  const formTopRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [applications, setApplications] = useState<any[]>([]);
  const [selectedApplication, setSelectedApplication] = useState<any>(null);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    visibility: 'private' as 'public' | 'private',
    status: 'planning' as Project['status'],
    priority: 'medium' as Project['priority'],
    category: '',
    tags: [] as string[],
    startDate: '',
    estimatedEndDate: '',
    parentId: '',
    projectType: 'project' as 'project' | 'subproject' | 'feature' | 'bug',
    sector: 'custom' as string,
    workMode: 'fluid' as 'strict' | 'fluid',
    ownerType: 'user' as 'user' | 'department' | 'team',
    projectOwnerName: '',
    projectOwnerEmail: '',
    applicationIds: [] as string[],
  });

  const [tagInput, setTagInput] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const projectsPath = user?.role === 'admin' ? '/admin/projects' : '/employee/projects';

  const clearFieldError = (field: string) =>
    setErrors(prev => { const e = { ...prev }; delete e[field]; return e; });

  useEffect(() => {
    fetchUsers();
    fetchApplications();

    const applicationId = searchParams.get('applicationId');
    const productId = searchParams.get('productId');

    if (applicationId || productId) {
      setFormData(prev => ({
        ...prev,
        ...(applicationId && { applicationIds: [applicationId] }),
        ...(productId && { parentId: productId }),
      }));
    }

    if (id) fetchProject();
  }, [id, searchParams]);

  const handleApplicationChange = (applicationId: string) => {
    const application = applications.find(app => app._id === applicationId);
    if (application) {
      setSelectedApplication(application);
      if (application.productIds?.length > 0) {
        const productId = application.productIds[0];
        setFormData(prev => ({ ...prev, applicationIds: [applicationId], parentId: productId }));
        fetchProductDetails(productId);
      } else {
        setFormData(prev => ({ ...prev, applicationIds: [applicationId], parentId: '' }));
      }
    } else {
      setSelectedApplication(null);
      setSelectedProduct(null);
      setFormData(prev => ({ ...prev, applicationIds: [], parentId: '' }));
    }
  };

  const fetchProductDetails = async (productId: string) => {
    try {
      const response = await productAPI.getProductById(productId);
      setSelectedProduct(response.data.data);
    } catch { /* non-critical */ }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    setUsersError('');
    try {
      const response = await projectAPI.getProjectUsers();
      setUsers(response.data || []);
    } catch (err: any) {
      setUsersError(err?.response?.data?.message || 'Failed to load team members');
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchApplications = async () => {
    try {
      const response = await applicationAPI.getAllApplications({ limit: 100 });
      const filteredApps = (response.data.data || []).filter((app: any) => {
        if (app.visibility === 'public') return true;
        if (user?.primaryDepartmentId && app.departmentId === user.primaryDepartmentId) return true;
        if (app.collaboratingDepartments?.some((cd: any) => cd.departmentId === user?.primaryDepartmentId)) return true;
        return false;
      });
      setApplications(filteredApps);
    } catch { /* non-critical */ }
  };

  const fetchProject = async () => {
    try {
      setLoading(true);
      const response = await projectAPI.getProjectById(id!);
      const project = response.data.data;
      setFormData({
        name: project.name,
        description: project.description,
        visibility: project.visibility,
        status: project.status,
        priority: project.priority,
        category: project.category || '',
        tags: project.tags || [],
        startDate: project.dates.startDate ? project.dates.startDate.split('T')[0] : '',
        estimatedEndDate: project.dates.estimatedEndDate ? project.dates.estimatedEndDate.split('T')[0] : '',
        parentId: project.parentId || '',
        projectType: project.projectType || 'project',
        sector: project.sector || 'custom',
        workMode: project.workMode || 'fluid',
        ownerType: project.ownerType || 'user',
        projectOwnerName: (project as any).projectOwner?.name || '',
        projectOwnerEmail: (project as any).projectOwner?.email || '',
        applicationIds: (project as any).applicationIds?.map((app: any) => app._id || app) || [],
      });
      setSelectedMembers(project.members.map((m: any) => m.userId));
    } catch (err: any) {
      setSubmitError(err.response?.data?.message || 'Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const parseApiError = (err: any): string => {
    const msg: string = err.response?.data?.message || err.message || '';
    if (msg.includes('E11000') || msg.includes('duplicate key'))
      return 'A project with this name already exists. Please choose a different name.';
    if (msg.includes('Cast to ObjectId failed'))
      return 'Invalid reference — please refresh and try again.';
    if (msg.includes('not a member of this organisation'))
      return 'One or more selected team members do not belong to this organisation.';
    return msg || 'Failed to save project. Please try again.';
  };

  const validate = (): Record<string, string> => {
    const e: Record<string, string> = {};

    if (!formData.name.trim())
      e.name = 'Project name is required';
    else if (formData.name.trim().length < 3)
      e.name = 'Project name must be at least 3 characters';

    if (!formData.description.trim())
      e.description = 'Description is required';
    else if (formData.description.trim().length < 10)
      e.description = 'Description must be at least 10 characters';

    if (!formData.status)
      e.status = 'Status is required';

    if (!formData.startDate)
      e.startDate = 'Start date is required';

    if (formData.estimatedEndDate && formData.startDate) {
      if (new Date(formData.estimatedEndDate) <= new Date(formData.startDate))
        e.estimatedEndDate = 'End date must be after the start date';
    }

    if (formData.projectOwnerName || formData.projectOwnerEmail) {
      if (!formData.projectOwnerName.trim())
        e.projectOwnerName = 'Owner name is required when email is provided';
      if (!formData.projectOwnerEmail.trim())
        e.projectOwnerEmail = 'Owner email is required when name is provided';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.projectOwnerEmail))
        e.projectOwnerEmail = 'Please enter a valid email address';
    }

    return e;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');

    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Scroll to top of form so user sees the errors
      formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const projectData = {
        ...formData,
        dates: {
          startDate: formData.startDate,
          estimatedEndDate: formData.estimatedEndDate,
        },
        ...(formData.projectOwnerName && formData.projectOwnerEmail && {
          projectOwner: {
            name: formData.projectOwnerName.trim(),
            email: formData.projectOwnerEmail.trim(),
          },
        }),
        members: selectedMembers.map(userId => {
          const u = users.find(u => u._id === userId || u.id === userId);
          return { userId, name: u?.name || '', role: 'contributor' as const, joinedAt: new Date().toISOString() };
        }),
      };

      if (id) {
        await projectAPI.updateProject(id, projectData);
      } else {
        await projectAPI.createProject(projectData);
      }

      navigate(projectsPath);
    } catch (err: any) {
      setSubmitError(parseApiError(err));
      formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !formData.tags.includes(trimmed)) {
      setFormData({ ...formData, tags: [...formData.tags, trimmed] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) =>
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });

  const toggleMember = (userId: string) =>
    setSelectedMembers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );

  const errorCount = Object.keys(errors).length;
  const inputBase = 'px-3 py-1 text-sm border rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500';
  const inputError = 'border-red-400 bg-red-50';
  const inputNormal = 'border-gray-200';

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-gray-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(projectsPath)}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-sm text-gray-500">{id ? 'Edit Project' : 'New Project'}</span>
        </div>
        <p className="text-xs text-gray-400">Fields marked <span className="text-red-500">*</span> are required</p>
      </div>

      <div className="max-w-4xl mx-auto px-8 py-6" ref={formTopRef}>
        {/* API / submit error */}
        {submitError && (
          <div className="mb-6 bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
            <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{submitError}</span>
          </div>
        )}

        {/* Validation summary */}
        {errorCount > 0 && (
          <div className="mb-6 bg-amber-50 border border-amber-300 text-amber-800 px-4 py-3 rounded-lg text-sm">
            <p className="font-semibold mb-1">Please fix {errorCount} error{errorCount > 1 ? 's' : ''} before continuing:</p>
            <ul className="list-disc list-inside space-y-0.5 text-xs">
              {Object.values(errors).map((msg, i) => <li key={i}>{msg}</li>)}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {/* Project Name */}
          <div className={errors.name ? 'mb-1' : 'mb-6'}>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Project name<Req />
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => { setFormData({ ...formData, name: e.target.value }); clearFieldError('name'); }}
              placeholder="Enter project name"
              className={`w-full text-2xl font-semibold text-gray-900 border-b-2 focus:outline-none bg-transparent py-1 transition-colors ${
                errors.name ? 'border-red-400' : 'border-gray-200 focus:border-blue-500'
              }`}
            />
            <FieldError msg={errors.name} />
          </div>

          {/* Properties Grid */}
          <div className="grid grid-cols-2 gap-x-8 gap-y-5 mb-8 text-sm">
            {/* Status */}
            <div>
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">Status<Req /></span>
              </div>
              <select
                value={formData.status}
                onChange={(e) => { setFormData({ ...formData, status: e.target.value as Project['status'] }); clearFieldError('status'); }}
                className={`${inputBase} ${errors.status ? inputError : inputNormal} font-medium cursor-pointer`}
              >
                <option value="planning">Planning</option>
                <option value="development">Development</option>
                <option value="testing">Testing</option>
                <option value="sit">SIT</option>
                <option value="uat">UAT</option>
                <option value="done">Done</option>
                <option value="production">Production</option>
              </select>
              <FieldError msg={errors.status} />
            </div>

            {/* Priority */}
            <div>
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                </svg>
                <span className="font-medium">Priority</span>
              </div>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value as Project['priority'] })}
                className={`${inputBase} ${inputNormal} cursor-pointer`}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>

            {/* Sector */}
            <div>
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <span className="font-medium">Sector</span>
              </div>
              <select
                value={formData.sector}
                onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
                className={`${inputBase} ${inputNormal} cursor-pointer`}
              >
                <option value="technology">Technology</option>
                <option value="sales">Sales</option>
                <option value="finance">Finance</option>
                <option value="logistics">Logistics</option>
                <option value="support">Customer Support</option>
                <option value="hr">Human Resources</option>
                <option value="marketing">Marketing</option>
                <option value="operations">Operations</option>
                <option value="custom">Custom / Other</option>
              </select>
            </div>

            {/* Work Mode */}
            <div>
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="font-medium">Work Mode</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'strict', label: 'Strict', desc: 'Sprint-governed, fixed dates, capacity enforced' },
                  { value: 'fluid', label: 'Fluid', desc: 'Self-directed, flexible dates, members manage own work' },
                ].map(m => (
                  <label
                    key={m.value}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      formData.workMode === m.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="workMode"
                      value={m.value}
                      checked={formData.workMode === m.value}
                      onChange={() => setFormData({ ...formData, workMode: m.value as any })}
                      className="mt-0.5"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{m.label}</div>
                      <div className="text-xs text-gray-500">{m.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Start Date */}
            <div>
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="font-medium">Start Date<Req /></span>
              </div>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => { setFormData({ ...formData, startDate: e.target.value }); clearFieldError('startDate'); }}
                className={`${inputBase} ${errors.startDate ? inputError : inputNormal}`}
              />
              <FieldError msg={errors.startDate} />
            </div>

            {/* End Date */}
            <div>
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="font-medium">End Date</span>
                <span className="text-xs text-gray-400">(optional)</span>
              </div>
              <input
                type="date"
                value={formData.estimatedEndDate}
                onChange={(e) => { setFormData({ ...formData, estimatedEndDate: e.target.value }); clearFieldError('estimatedEndDate'); }}
                min={formData.startDate || undefined}
                className={`${inputBase} ${errors.estimatedEndDate ? inputError : inputNormal}`}
              />
              <FieldError msg={errors.estimatedEndDate} />
            </div>

            {/* Visibility */}
            <div>
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <span className="font-medium">Visibility</span>
              </div>
              <select
                value={formData.visibility}
                onChange={(e) => setFormData({ ...formData, visibility: e.target.value as 'public' | 'private' })}
                className={`${inputBase} ${inputNormal} cursor-pointer`}
              >
                <option value="private">Private</option>
                <option value="public">Public</option>
              </select>
            </div>

            {/* Category */}
            <div>
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                <span className="font-medium">Category</span>
                <span className="text-xs text-gray-400">(optional)</span>
              </div>
              <input
                type="text"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="e.g., Web, Mobile"
                className={`${inputBase} ${inputNormal}`}
              />
            </div>
          </div>

          {/* Description */}
          <div className="mb-8">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Description<Req />
              <span className="ml-2 text-xs font-normal text-gray-400">Minimum 10 characters</span>
            </label>
            <textarea
              rows={5}
              value={formData.description}
              onChange={(e) => { setFormData({ ...formData, description: e.target.value }); clearFieldError('description'); }}
              placeholder="Describe the project — its purpose, goals, and scope..."
              className={`w-full px-4 py-3 text-sm text-gray-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none transition-colors ${
                errors.description ? 'border-red-400 bg-red-50' : 'border-gray-200'
              }`}
            />
            <div className="flex items-center justify-between mt-1">
              <FieldError msg={errors.description} />
              <span className={`text-xs ml-auto ${
                formData.description.length < 10 && formData.description.length > 0
                  ? 'text-red-500'
                  : 'text-gray-400'
              }`}>
                {formData.description.length} chars
              </span>
            </div>
          </div>

          {/* Application & Product */}
          {!searchParams.get('applicationId') && (
            <div className="mb-8">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Link to Application
                <span className="ml-2 text-xs font-normal text-gray-400">(optional)</span>
              </label>
              <select
                value={formData.applicationIds[0] || ''}
                onChange={(e) => handleApplicationChange(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">None — Standalone project</option>
                {applications.map((app) => (
                  <option key={app._id} value={app._id}>
                    {app.name} ({app.applicationType})
                  </option>
                ))}
              </select>
              {selectedProduct && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 flex items-center gap-2">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  <span><strong>Product:</strong> {selectedProduct.name}</span>
                </div>
              )}
            </div>
          )}

          {/* Project Owner */}
          <div className="mb-8">
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Project Owner
              <span className="ml-2 text-xs font-normal text-gray-400">(optional — for external stakeholders)</span>
            </label>
            <p className="text-xs text-gray-500 mb-3">
              Specify a client or stakeholder as the named project owner, separate from the system user creating this project.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Full name</label>
                <input
                  type="text"
                  value={formData.projectOwnerName}
                  onChange={(e) => { setFormData({ ...formData, projectOwnerName: e.target.value }); clearFieldError('projectOwnerName'); }}
                  placeholder="e.g. Jane Smith"
                  className={`w-full ${inputBase} ${errors.projectOwnerName ? inputError : inputNormal}`}
                />
                <FieldError msg={errors.projectOwnerName} />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Email address</label>
                <input
                  type="email"
                  value={formData.projectOwnerEmail}
                  onChange={(e) => { setFormData({ ...formData, projectOwnerEmail: e.target.value }); clearFieldError('projectOwnerEmail'); }}
                  placeholder="jane.smith@client.com"
                  className={`w-full ${inputBase} ${errors.projectOwnerEmail ? inputError : inputNormal}`}
                />
                <FieldError msg={errors.projectOwnerEmail} />
              </div>
            </div>
          </div>

          {/* Tags */}
          <div className="mb-8">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Tags
              <span className="ml-2 text-xs font-normal text-gray-400">(optional)</span>
            </label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                placeholder="Type a tag and press Enter or Add"
                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors"
              >
                Add
              </button>
            </div>
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-sm flex items-center gap-1.5"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="text-blue-400 hover:text-blue-700 leading-none"
                      aria-label={`Remove tag ${tag}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Team Members */}
          <div className="mb-8">
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Team Members
              <span className="ml-2 text-xs font-normal text-gray-400">(optional)</span>
              {selectedMembers.length > 0 && (
                <span className="ml-2 text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                  {selectedMembers.length} selected
                </span>
              )}
            </label>
            <p className="text-xs text-gray-500 mb-3">Select team members who will work on this project.</p>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              {usersLoading ? (
                <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
                  <svg className="animate-spin w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Loading team members...
                </div>
              ) : usersError ? (
                <div className="flex items-center justify-center gap-2 py-10 text-sm text-red-600">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {usersError}
                  <button
                    type="button"
                    onClick={fetchUsers}
                    className="ml-2 text-blue-600 underline hover:no-underline text-xs"
                  >
                    Retry
                  </button>
                </div>
              ) : users.filter(u => (u._id || u.id) !== user?._id && (u._id || u.id) !== user?.id).length === 0 ? (
                <div className="text-center py-10 text-sm text-gray-400">
                  No other team members available in this organisation
                </div>
              ) : (
                <div className="max-h-64 overflow-y-auto">
                  {users
                    .filter(u => (u._id || u.id) !== user?._id && (u._id || u.id) !== user?.id)
                    .map((availableUser) => {
                      const uid = availableUser._id || availableUser.id || '';
                      const checked = selectedMembers.includes(uid);
                      return (
                        <label
                          key={uid}
                          className={`flex items-center gap-3 p-3 cursor-pointer border-b border-gray-100 last:border-0 transition-colors ${
                            checked ? 'bg-blue-50' : 'hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleMember(uid)}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 ${
                            checked ? 'bg-blue-600' : 'bg-gray-400'
                          }`}>
                            {availableUser.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{availableUser.name}</p>
                            <p className="text-xs text-gray-500 truncate">{availableUser.email}</p>
                          </div>
                          {checked && (
                            <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </label>
                      );
                    })}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate(projectsPath)}
              className="px-6 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-blue-300 transition-colors flex items-center gap-2"
            >
              {loading && (
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
              {loading ? 'Saving...' : id ? 'Update Project' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectForm;
